"use client"

import React, { useState } from "react"
import { ChevronDown, Globe } from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/shadcn/components/ui/avatar"
import { Button } from "@/shadcn/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shadcn/components/ui/card"
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shadcn/components/ui/command"
import { Input } from "@/shadcn/components/ui/input"
import { Label } from "@/shadcn/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn/components/ui/popover"
import { cn } from "@/lib/utils"

import AvatarDialog from "./AvatarDialog"
import {
  CARD,
  CARD_DESCRIPTION,
  CARD_HEADER,
  CARD_TITLE,
  SM_BUTTON,
} from "./styles"

export default function ProfileCard(props: any) {
  const showOrgAndTimezone = props.appName === "dlvr"
  const [timezone, setTimezone] = useState(props.user?.detail?.timezone)
  const [tzOpen, setTzOpen] = useState(false)

  const timezones = props.utilities?.timezones || []

  return (
    <Card className={CARD}>
      <CardHeader className={CARD_HEADER}>
        <CardTitle className={CARD_TITLE}>Profile</CardTitle>
        <CardDescription className={CARD_DESCRIPTION}>
          Customize your profile settings.
        </CardDescription>
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:gap-0 tw:p-0">
        <div
          className={cn(
            "tw:flex tw:w-full tw:items-center tw:gap-4 tw:px-4",
            !showOrgAndTimezone && "tw:pb-4"
          )}
        >
          <Avatar className="tw:size-10 tw:after:hidden">
            <AvatarImage src={props.user?.detail?.avatar} alt="" />
            <AvatarFallback className="tw:bg-muted tw:text-sm tw:font-medium tw:text-muted-foreground">
              YOU
            </AvatarFallback>
          </Avatar>
          <AvatarDialog
            avatar={props.user?.detail?.avatar}
            onSaveAvatar={props.onSaveAvatar}
            resetAvatar={props.resetAvatar}
            trigger={
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(SM_BUTTON, "tw:w-fit")}
              >
                Change avatar
              </Button>
            }
          />
        </div>
        {showOrgAndTimezone && (
          <div className="tw:grid tw:w-full tw:grid-cols-1 tw:items-start tw:gap-3 tw:p-4 tw:md:grid-cols-2">
            <div className="tw:flex tw:flex-col tw:gap-2">
              <Label
                htmlFor="org-name"
                className="tw:text-sm tw:font-normal tw:leading-5 tw:text-accent-foreground"
              >
                Organization name
              </Label>
              <Input
                id="org-name"
                defaultValue={props?.user?.detail?.organization}
                onChange={props.debouncedUpdater}
                data-field="organization"
                data-immediate={true}
                className="tw:h-8 tw:rounded-lg tw:px-2.5 tw:py-1 tw:text-sm tw:font-normal tw:leading-5"
              />
            </div>
            <div className="tw:flex tw:flex-col tw:gap-2">
              <Label
                htmlFor="timezone"
                className="tw:text-sm tw:font-normal tw:leading-5 tw:text-accent-foreground"
              >
                Timezone
              </Label>
              <Popover open={tzOpen} onOpenChange={setTzOpen}>
                <PopoverTrigger asChild>
                  <button
                    id="timezone"
                    type="button"
                    role="combobox"
                    aria-expanded={tzOpen}
                    className="tw:flex tw:h-8 tw:w-full tw:items-center tw:gap-1.5 tw:rounded-lg tw:border tw:border-input tw:bg-background tw:py-1 tw:pl-2.5 tw:pr-1 tw:text-sm tw:font-normal tw:leading-5 tw:text-popover-foreground"
                  >
                    <Globe
                      className="tw:size-4 tw:shrink-0 tw:opacity-50"
                      aria-hidden
                    />
                    <span className="tw:flex-1 tw:truncate tw:text-left">
                      {timezone}
                    </span>
                    <span className="tw:flex tw:size-6 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-md">
                      <ChevronDown className="tw:size-3" aria-hidden />
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="tw:w-[var(--radix-popover-trigger-width)] tw:rounded-lg tw:border tw:border-border tw:bg-popover tw:p-0 tw:shadow-md tw:[&_[data-slot=input-group-addon]]:hidden"
                >
                  <Command>
                    <CommandInput
                      placeholder="Search"
                      className="tw:text-sm tw:font-normal tw:leading-5 tw:placeholder:text-muted-foreground"
                    />
                    <CommandList className="tw:p-1">
                      <CommandEmpty className="tw:py-6 tw:text-center tw:text-sm tw:text-muted-foreground">
                        No timezone found.
                      </CommandEmpty>
                      {timezones.map((tz: any) => (
                        <CommandItem
                          key={tz.id}
                          value={tz.id}
                          onSelect={() => {
                            setTimezone(tz.id)
                            setTzOpen(false)
                            props.onTimezoneUpdate(tz.id)
                          }}
                          className="tw:gap-2 tw:rounded-md tw:py-1 tw:pl-1.5 tw:pr-8 tw:text-sm tw:font-normal tw:leading-5 tw:text-popover-foreground tw:[&>svg]:hidden"
                        >
                          <span className="tw:flex-1 tw:truncate">{tz.name}</span>
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
